import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSalePage } from '../../contexts/SalePageContext';
import SalePageNavbar from '../../components/SalePageNavbar';
import { 
  Globe, 
  Calendar, 
  Package, 
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  Info,
  Loader2,
  Edit2,
  ShoppingCart
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { validateUsername, sanitizeUsername } from '../../utils/usernameValidator';
import { checkUsernameAvailability } from '../../services/usernameService';

interface UrlAddonData {
  status: 'active' | 'expired' | 'pending';
  username?: string;
  type: 'path' | 'subdomain' | 'both';
  endDate?: any;
  createdAt?: any;
}

export default function SalePageUrlAddonManagement() {
  const { user } = useAuthContext();
  const { subscription, loading: salePageLoading } = useSalePage();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [hasUrlAddon, setHasUrlAddon] = useState(false);
  const [urlAddonData, setUrlAddonData] = useState<UrlAddonData | null>(null);
  const [selectedPathUrl, setSelectedPathUrl] = useState(false);
  const [selectedSubdomain, setSelectedSubdomain] = useState(false);
  const [urlAddonUsername, setUrlAddonUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [usernameCheckTimeout, setUsernameCheckTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        // Check if user has URL add-on for salepage
        const urlAddonDoc = await getDoc(doc(db, 'salePageUrlAddons', user.uid));
        if (urlAddonDoc.exists()) {
          const data = urlAddonDoc.data() as UrlAddonData;
          setUrlAddonData(data);
          if (data.status === 'active') {
            setHasUrlAddon(true);
            if (data.username) {
              setUrlAddonUsername(data.username);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, navigate]);

  const formatDate = (date: any) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getDaysUntilExpiry = (expiryDate: any) => {
    if (!expiryDate) return null;
    const expiry = expiryDate.toDate ? expiryDate.toDate() : new Date(expiryDate);
    const now = new Date();
    const diff = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const calculateProRatePrice = () => {
    if (!subscription?.endDate) return null;

    const now = new Date();
    const endDate = subscription.endDate.toDate ? subscription.endDate.toDate() : new Date(subscription.endDate);
    const daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysRemaining <= 0) return null;

    // Determine billing cycle from plan
    const isYearly = subscription.plan?.includes('yearly');
    const isSemiAnnual = subscription.plan?.includes('semi_annual');
    const isProPlusYearly = subscription.plan === 'pro_plus_yearly';
    
    // Get full cycle price (with discounts)
    const fullCyclePrice = getFullCyclePrice();
    
    // Calculate total days based on actual paid months (after discount)
    const totalDays = isYearly ? 300 : isSemiAnnual ? 150 : 30;
    
    // Calculate price per day
    const pricePerDay = fullCyclePrice / totalDays;
    
    // Calculate pro-rated price
    const totalPrice = Math.ceil(pricePerDay * daysRemaining);

    return {
      daysRemaining,
      totalPrice,
      isProRate: true,
      billingCycle: isYearly ? 'yearly' : isSemiAnnual ? 'semi_annual' : 'monthly'
    };
  };

  const getFullCyclePrice = () => {
    const isYearly = subscription?.plan?.includes('yearly');
    const isSemiAnnual = subscription?.plan?.includes('semi_annual');
    const isProPlusYearly = subscription?.plan === 'pro_plus_yearly';

    let totalPrice = 0;
    
    // Path URL pricing with discount
    if (selectedPathUrl) {
      if (isYearly) {
        totalPrice += 49 * 10; // 12 months - 2 months free = 10 months
      } else if (isSemiAnnual) {
        totalPrice += 49 * 5; // 6 months - 1 month free = 5 months
      } else {
        totalPrice += 49;
      }
    }
    
    // Subdomain pricing with discount (free for Pro+ Yearly)
    if (selectedSubdomain) {
      if (!isProPlusYearly) {
        if (isYearly) {
          totalPrice += 99 * 10; // 12 months - 2 months free = 10 months
        } else if (isSemiAnnual) {
          totalPrice += 99 * 5; // 6 months - 1 month free = 5 months
        } else {
          totalPrice += 99;
        }
      }
    }

    return totalPrice;
  };

  const handleProceedToPayment = () => {
    const proRateInfo = calculateProRatePrice();
    
    if (!proRateInfo) {
      alert('ไม่สามารถคำนวณราคาได้ กรุณาลองใหม่อีกครั้ง');
      return;
    }

    if (!urlAddonUsername || usernameError || !usernameAvailable) {
      alert('กรุณากรอก Username ที่ถูกต้อง');
      return;
    }

    const urlAddons = [];
    if (selectedPathUrl) {
      urlAddons.push({ type: 'path', username: urlAddonUsername });
    }
    if (selectedSubdomain) {
      urlAddons.push({ type: 'subdomain', username: urlAddonUsername });
    }

    const checkoutData = {
      plan: subscription?.plan || 'free',
      planName: subscription?.plan || 'FREE',
      planPrice: 0,
      urlAddons: urlAddons,
      total: proRateInfo.totalPrice,
      isRenewal: false,
      isUrlAddonOnly: true,
      isSalePage: true,
      proRateInfo: {
        daysRemaining: proRateInfo.daysRemaining,
        originalPrice: getFullCyclePrice(),
        proRatedPrice: proRateInfo.totalPrice,
        billingCycle: proRateInfo.billingCycle
      }
    };

    console.log('💾 [SalePageUrlAddonManagement] Saving to localStorage:', checkoutData);
    localStorage.setItem('salePageCheckout', JSON.stringify(checkoutData));
    
    navigate(`/salepage/checkout?plan=${subscription?.plan || 'free'}&addon=url`);
  };

  const daysLeft = subscription?.endDate ? getDaysUntilExpiry(subscription.endDate) : null;
  const proRateInfo = calculateProRatePrice();
  const isProPlusYearly = subscription?.plan === 'pro_plus_yearly';

  if (loading) {
    return (
      <>
        <SalePageNavbar />
        <div className="min-h-screen bg-[#F3F8F8] flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="animate-spin h-12 w-12 text-emerald-600 mx-auto" />
            <p className="mt-4 text-gray-600">กำลังโหลด...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SalePageNavbar />
      <div className="min-h-screen bg-[#F3F8F8] py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/salepage/settings')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              กลับไปหน้า Settings
            </button>
            
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Globe className="w-8 h-8 text-orange-600" />
              จัดการ URL Add-on
            </h1>
            <p className="text-gray-600 mt-2">จัดการ URL สำหรับหน้าขายสินค้าของคุณ</p>
          </div>

        {/* Current Package Info */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border-2 border-emerald-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-emerald-600" />
            แพ็คเก็จปัจจุบัน
          </h2>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">แพ็คเกจ</p>
              <p className="text-lg font-bold text-gray-900">
                {subscription?.plan ? subscription.plan.toUpperCase() : 'ทดสอบระบบ'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">หมดอายุ</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatDate(subscription?.endDate)}
              </p>
              {daysLeft !== null && (
                <p className={`text-xs font-bold ${
                  daysLeft <= 0 ? 'text-red-600' : daysLeft <= 7 ? 'text-amber-600' : 'text-gray-600'
                }`}>
                  {daysLeft <= 0 ? `หมดอายุแล้ว ${Math.abs(daysLeft)} วัน` : `เหลืออีก ${daysLeft} วัน`}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">รอบบิล</p>
              <p className="text-lg font-semibold text-gray-900">
                {subscription?.plan?.includes('yearly') ? 'รายปี' : subscription?.plan?.includes('semi_annual') ? '6 เดือน' : 'รายเดือน'}
              </p>
            </div>
          </div>

          {/* Important Notice */}
          <div className="mt-4 bg-emerald-50 border-2 border-emerald-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-emerald-900 mb-1">
                  ℹ️ เนื่องจากคุณสมัครแพ็คเก็จ {subscription?.plan ? subscription.plan.toUpperCase() : 'ทดสอบระบบ'}
                </p>
                <p className="text-sm text-emerald-700">
                  URL Add-on จะต่ออายุ{subscription?.plan?.includes('yearly') ? 'รายปี' : subscription?.plan?.includes('semi_annual') ? 'ราย 6 เดือน' : 'รายเดือน'}พร้อมกับแพ็คเก็จหลัก
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Current URL Add-on Status */}
        {hasUrlAddon && urlAddonData && (
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-lg p-6 mb-6 border-2 border-green-200">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  URL Add-on ปัจจุบัน
                </h2>
                <p className="text-sm text-gray-600 mt-1">คุณมี URL Add-on ที่ใช้งานอยู่</p>
              </div>
              <span className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full">
                ใช้งานอยู่
              </span>
            </div>

            <div className="space-y-3">
              <div className="bg-white rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Username</p>
                <p className="text-lg font-bold text-gray-900">{urlAddonData.username}</p>
              </div>

              <div className="bg-white rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-2">URL ของคุณ</p>
                <div className="space-y-2">
                  {(urlAddonData.type === 'path' || urlAddonData.type === 'both') && (
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-orange-600" />
                      <a 
                        href={`https://saledi.web.app/${urlAddonData.username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-600 hover:text-orange-700 font-semibold underline"
                      >
                        saledi.web.app/{urlAddonData.username}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">หมดอายุ</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDate(urlAddonData.endDate)}
                </p>
              </div>
            </div>

            <button
              onClick={() => navigate('/salepage/settings')}
              className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-semibold transition-all"
            >
              <Edit2 className="w-4 h-4" />
              จัดการ URL Add-on
            </button>
          </div>
        )}

        {/* Purchase URL Add-on */}
        {!hasUrlAddon && (
          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-orange-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4">ซื้อ URL Add-on</h2>

            {/* URL Type Selection (Path only — subdomain not supported on saledi.web.app) */}
            <div className="grid grid-cols-1 gap-4 mb-6">
              {/* Path URL */}
              <button
                onClick={() => setSelectedPathUrl(!selectedPathUrl)}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  selectedPathUrl
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-orange-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-gray-900">Path URL</h3>
                  {selectedPathUrl && <CheckCircle className="w-5 h-5 text-orange-600" />}
                </div>
                <p className="text-sm text-gray-600 mb-3">saledi.web.app/yourname</p>
                <div className="space-y-1 text-xs text-gray-600">
                  <p>• รายเดือน: <span className="font-semibold">฿49/เดือน</span></p>
                  <p>• 6 เดือน: <span className="font-semibold">฿245</span> <span className="text-green-600">(ฟรี 1 เดือน)</span></p>
                  <p>• รายปี: <span className="font-semibold">฿490</span> <span className="text-green-600">(ฟรี 2 เดือน)</span></p>
                </div>
              </button>
            </div>

            {/* Username Input */}
            {(selectedPathUrl || selectedSubdomain) && (
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border-2 border-orange-200 mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  ชื่อ URL ที่ต้องการ
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={urlAddonUsername}
                    onChange={(e) => {
                      const sanitized = sanitizeUsername(e.target.value);
                      setUrlAddonUsername(sanitized);
                      setUsernameError('');
                      setUsernameAvailable(null);
                      
                      if (usernameCheckTimeout) {
                        clearTimeout(usernameCheckTimeout);
                      }
                      
                      if (!sanitized) return;
                      
                      const validation = validateUsername(sanitized);
                      if (!validation.isValid) {
                        setUsernameError(validation.error || 'Username ไม่ถูกต้อง');
                        setUsernameAvailable(false);
                        return;
                      }
                      
                      const timeout = setTimeout(async () => {
                        setIsCheckingUsername(true);
                        try {
                          const result = await checkUsernameAvailability(sanitized, user?.uid);
                          if (result.available) {
                            setUsernameError('');
                            setUsernameAvailable(true);
                          } else {
                            setUsernameError(result.message);
                            setUsernameAvailable(false);
                          }
                        } catch (error) {
                          console.error('Error checking username:', error);
                          setUsernameError('ไม่สามารถตรวจสอบ Username ได้');
                          setUsernameAvailable(null);
                        } finally {
                          setIsCheckingUsername(false);
                        }
                      }, 500);
                      
                      setUsernameCheckTimeout(timeout);
                    }}
                    placeholder="ชื่อของคุณ"
                    className={`w-full px-4 py-3 pr-12 border-2 rounded-lg focus:outline-none transition-colors ${
                      usernameError 
                        ? 'border-red-300 focus:border-red-500' 
                        : usernameAvailable 
                        ? 'border-green-300 focus:border-green-500'
                        : 'border-orange-300 focus:border-orange-500'
                    }`}
                  />
                  {isCheckingUsername && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="w-5 h-5 text-orange-600 animate-spin" />
                    </div>
                  )}
                  {!isCheckingUsername && usernameAvailable === true && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                  )}
                  {!isCheckingUsername && usernameAvailable === false && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    </div>
                  )}
                </div>
                
                {usernameError && (
                  <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {usernameError}
                  </p>
                )}
                
                {!usernameError && usernameAvailable && (
                  <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Username นี้ใช้งานได้!
                  </p>
                )}
                
                {/* URL Preview */}
                <div className="mt-3 space-y-1">
                  {selectedPathUrl && (
                    <p className="text-xs text-gray-600">
                      Path URL: <span className="font-semibold text-orange-600">
                        saledi.web.app/{urlAddonUsername || 'username'}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Pro-rate Pricing Summary */}
            {(selectedPathUrl || selectedSubdomain) && proRateInfo && (
              <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-xl p-6 border-2 border-emerald-200 mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">สรุปค่าบริการ</h3>
                
                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">ระยะเวลาที่เหลือ:</span>
                    <span className="font-semibold text-gray-900">{proRateInfo.daysRemaining} วัน</span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">ราคาเต็มรอบ:</span>
                    <span className="text-gray-400 line-through">฿{getFullCyclePrice().toLocaleString()}</span>
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t-2 border-emerald-200">
                    <span className="text-lg font-semibold text-gray-900">ยอดชำระ (Pro-rate):</span>
                    <span className="text-3xl font-bold text-emerald-600">
                      ฿{proRateInfo.totalPrice.toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-xs text-emerald-800">
                    💡 คุณจ่ายเฉพาะระยะเวลาที่เหลือ ({proRateInfo.daysRemaining} วัน) หลังจากนี้ URL Add-on จะต่ออายุพร้อมกับแพ็คเก็จหลักทุกครั้ง
                  </p>
                </div>
              </div>
            )}

            {/* Purchase Button */}
            {(selectedPathUrl || selectedSubdomain) && (
              <button
                onClick={handleProceedToPayment}
                disabled={!urlAddonUsername || !!usernameError || !usernameAvailable}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ShoppingCart className="w-5 h-5" />
                ดำเนินการชำระเงิน - ฿{proRateInfo?.totalPrice.toLocaleString()}
              </button>
            )}
          </div>
        )}
        </div>
      </div>
    </>
  );
}
